/**
 * Curated Paper + Velocity plugin catalog for Uliunai Survival.
 * Hand-authored from minecraft/server/versions.env (source of truth for pins).
 * Retired server plugins are deliberately omitted from this public catalog.
 * Not auto-synced — update this list when versions.env plugins change.
 *
 * thumbnailUrl values are Modrinth project icons (cdn.modrinth.com) where available.
 * NanoLimbo has no Modrinth page — falls back to the GitHub owner avatar.
 */

export type PluginCategory =
  | 'Claims'
  | 'Permissions'
  | 'Economy'
  | 'World'
  | 'Auth'
  | 'QoL'
  | 'Library'
  | 'Proxy'

export interface VanillaPlugin {
  name: string
  version: string
  category: PluginCategory
  blurb: string
  url?: string
  /** Project icon (Modrinth CDN or similar). Optional — UI falls back to a package glyph. */
  thumbnailUrl?: string
}

export const pluginCategories: PluginCategory[] = [
  'Claims',
  'Permissions',
  'Economy',
  'World',
  'Auth',
  'QoL',
  'Library',
  'Proxy',
]

export const vanillaPlugins: VanillaPlugin[] = [
  {
    name: 'SimpleClaimSystem',
    version: '1.13.1',
    category: 'Claims',
    blurb: 'Player land claims so builds stay protected without grief.',
    url: 'https://modrinth.com/plugin/simpleclaimsystem',
    thumbnailUrl:
      'https://cdn.modrinth.com/data/80Ke0mYG/d9cf637da756d12d3e43f3cebf6731ea60af9f71_96.webp',
  },
  {
    name: 'LuckPerms',
    version: '5.5.53',
    category: 'Permissions',
    blurb: 'Groups and permissions for members, builders, moderators, and admins.',
    url: 'https://modrinth.com/plugin/luckperms',
    thumbnailUrl:
      'https://cdn.modrinth.com/data/Vebnzrzj/90943902cc650e95b167265b8f2d7c893f82c8f4_96.webp',
  },
  {
    name: 'EssentialsX',
    version: '2.22.0',
    category: 'QoL',
    blurb: 'Essential survival commands — homes, teleports, kits, and more.',
    url: 'https://modrinth.com/plugin/essentialsx',
    thumbnailUrl:
      'https://cdn.modrinth.com/data/hXiIvTyT/e621675be1d0421b43b65ab8082507532d937009_96.webp',
  },
  {
    name: 'CoreProtect',
    version: '24.0',
    category: 'World',
    blurb: 'Block logging and staff rollback when something goes wrong.',
    url: 'https://modrinth.com/plugin/coreprotect',
    thumbnailUrl:
      'https://cdn.modrinth.com/data/Lu3KuzdV/b2c4b7b0033ab09cc166f2848003ef3a02c70a83.png',
  },
  {
    name: 'WorldEdit',
    version: '7.4.4',
    category: 'World',
    blurb: 'Selection and edit tools for trusted builders.',
    url: 'https://modrinth.com/plugin/worldedit',
    thumbnailUrl:
      'https://cdn.modrinth.com/data/1u6JkXh5/30698991048ced77e60c4e8284007d3782f2e6a3_96.webp',
  },
  {
    name: 'WorldGuard',
    version: '7.0.17',
    category: 'World',
    blurb: 'Region protection for lobby, spawn, and staff builds.',
    url: 'https://modrinth.com/plugin/worldguard',
    thumbnailUrl:
      'https://cdn.modrinth.com/data/DKY9btbd/e1281ffadc4e194780bd31eb89ae6ea2537ec41b_96.webp',
  },
  {
    name: 'Multiverse-Core',
    version: '5.7.2',
    category: 'World',
    blurb: 'Multiple worlds under one server (overworld, lobby, and more).',
    url: 'https://modrinth.com/plugin/multiverse-core',
    thumbnailUrl:
      'https://cdn.modrinth.com/data/3wmN97b8/3e3c705351a85c68b78a6131aff23f4674b2f71f_96.webp',
  },
  {
    name: 'Multiverse-Portals',
    version: '5.2.3',
    category: 'World',
    blurb: 'Portal regions that teleport into Multiverse destinations.',
    url: 'https://modrinth.com/plugin/multiverse-portals',
    thumbnailUrl:
      'https://cdn.modrinth.com/data/8VMk6P0I/3e3c705351a85c68b78a6131aff23f4674b2f71f_96.webp',
  },
  {
    name: 'VoidGen',
    version: '2.3.8',
    category: 'World',
    blurb: 'Void world generator used for Multiverse create/import (e.g. lobby).',
    url: 'https://modrinth.com/plugin/voidgen',
    thumbnailUrl:
      'https://cdn.modrinth.com/data/DCTiMOWF/b33bc2cd7c7f5745bacd956838c50b25078f7d11_96.webp',
  },
  {
    name: 'NpcPlugin',
    version: '2.4.3',
    category: 'QoL',
    blurb: 'NPCs for guides, shops, and lobby helpers on Paper.',
    url: 'https://modrinth.com/plugin/npc-plugin',
    thumbnailUrl:
      'https://cdn.modrinth.com/data/lMVQTkWl/8e1b68d4ed96b6319d918aa5fbd7572ee3ce6ebd_96.webp',
  },
  {
    name: 'VaultUnlocked',
    version: '2.20.2',
    category: 'Library',
    blurb: 'Vault API shim so economy plugins can register providers.',
    url: 'https://modrinth.com/plugin/vaultunlocked',
    thumbnailUrl:
      'https://cdn.modrinth.com/data/ayRaM8J7/75b4141b5e316a73a0372e895bb24e09122759f1_96.webp',
  },
  {
    name: 'EzEconomy',
    version: '3.0.4',
    category: 'Economy',
    blurb: 'Server economy provider used by player and server shops.',
    url: 'https://modrinth.com/plugin/ezeconomy',
    thumbnailUrl:
      'https://cdn.modrinth.com/data/rQtkf57Y/cbd3b0a72ee73c8752e90285645583f68028df58_96.webp',
  },
  {
    name: 'EzShops',
    version: '2.5.9',
    category: 'Economy',
    blurb: 'GUI shops for players and the server economy.',
    url: 'https://modrinth.com/plugin/ezshops',
    thumbnailUrl:
      'https://cdn.modrinth.com/data/zaW55ehx/9535572b74b595889981ded5e60e6d9f9c504f7c_96.webp',
  },
  {
    name: 'StarterLootChest',
    version: '2.0.0',
    category: 'QoL',
    blurb: 'Per-player loot chests — starter kits, VIP chests, and farmable loot.',
    url: 'https://modrinth.com/plugin/lootchest',
    thumbnailUrl:
      'https://cdn.modrinth.com/data/PuMJcBlY/89cbdc61b4518c2729bdc0e656135e9d5a95cf86_96.webp',
  },
  {
    name: 'TAB',
    version: '6.1.0',
    category: 'QoL',
    blurb: 'Tab list and nametags, including LuckPerms group prefixes.',
    url: 'https://modrinth.com/plugin/tab-was-taken',
    thumbnailUrl:
      'https://cdn.modrinth.com/data/gG7VFbG0/4a7fcab5db748296adac920926efff93b1da416d_96.webp',
  },
  {
    name: 'PlaceholderAPI',
    version: '2.12.3',
    category: 'Library',
    blurb: 'Placeholder expansions used by TAB, tags, and other plugins.',
    url: 'https://modrinth.com/plugin/placeholderapi',
    thumbnailUrl:
      'https://cdn.modrinth.com/data/lKEzGugV/4ae5b09229ece3e8d10c113ffe09361adafc7dc6_96.webp',
  },
  {
    name: 'NBT-API',
    version: '2.15.7',
    category: 'Library',
    blurb: 'NBT library required by SupremeTags and related plugins.',
    url: 'https://modrinth.com/plugin/nbtapi',
    thumbnailUrl: 'https://cdn.modrinth.com/data/nfGCP9fk/icon.png',
  },
  {
    name: 'SupremeTags',
    version: '2.1.18',
    category: 'QoL',
    blurb: 'Cosmetic selectable tags with a player GUI.',
    url: 'https://modrinth.com/plugin/supremetags',
    thumbnailUrl:
      'https://cdn.modrinth.com/data/mzraaPLu/a7a92642a84774879f846ad64f9f193a1ef567b2_96.webp',
  },
  {
    name: 'LibreLoginProd',
    version: '0.25.10-prod',
    category: 'Auth',
    blurb:
      'Hybrid online/offline auth on the Velocity proxy (not the Paper backend). Supports premium and cracked logins.',
    url: 'https://modrinth.com/plugin/libreloginprod',
    thumbnailUrl:
      'https://cdn.modrinth.com/data/hWYRE2rd/8b648e1a1f789cbc837a3f3e1b1e243ed44ea8aa_96.webp',
  },
  {
    name: 'NanoLimbo',
    version: '1.13.0',
    category: 'Proxy',
    blurb:
      'Auth limbo waiting room for /register and /login — runs beside Velocity, not on the survival world.',
    url: 'https://github.com/Nan1t/NanoLimbo',
    // No Modrinth listing — GitHub owner avatar as a stable public image.
    thumbnailUrl: 'https://avatars.githubusercontent.com/u/52887197?v=4&s=96',
  },
]
